import React from "react";
import { FilterContext } from "../components/Filters/FilterContext";

interface NodeWithFilters {
  state: any;
  year: number | null;
  member: {
    id: string;
    name: string;
  } | null;
  plannedDate: string | null;
  managementPackageVariant?: {
    code: string;
  } | null;
}

interface Edge<T> {
  node: T | null;
}

interface PlannedDate {
  date: string;
  state: string;
}

interface DataWithEdges<T> {
  readonly edges?: ReadonlyArray<Edge<T> | null>;
  readonly edgeCount?: number | null;
  readonly totalCount?: number | null;
}

function useFilteredData<T extends NodeWithFilters>(
  data: DataWithEdges<T> | null
) {
  const filters = React.useContext(FilterContext);
  const filteredEdges = React.useMemo(() => {
    return (
      data?.edges?.filter((edge) => {
        const yearMatches = filters?.years?.length
          ? filters.years.includes(edge?.node?.year || 9999)
          : true;
        const member = edge?.node?.member;
        const memberMatches = filters?.members?.length
          ? filters.members.includes(member?.id || "Onbekende Deelnemer")
          : true;

        const projectMatches = filters?.projects?.length
          ? filters.projects.includes(
              edge?.node?.managementPackageVariant?.code ||
                "Onbekend Beheerpakket"
            )
          : true;
        const plannedDatesMatches = filters?.plannedDates?.length
          ? filters.plannedDates.includes(edge?.node?.plannedDate || "1234")
          : true;

        return (
          yearMatches && memberMatches && plannedDatesMatches && projectMatches
        );
      }) || []
    );
  }, [
    data?.edges,
    filters.years,
    filters.members,
    filters.projects,
    filters.plannedDates,
  ]);

  return filteredEdges;
}

function usePopulateFilters<T extends NodeWithFilters>(
  data: DataWithEdges<T> | null
) {
  const { setAllYears, setAllMembers, setAllProjects, setAllPlannedDates } =
    React.useContext(FilterContext);

  React.useEffect(() => {
    const members = data?.edges?.reduce((acc, edge) => {
      const member = edge?.node?.member;
      if (member && !acc.some((m) => m.id === member.id)) {
        acc.push({ id: member.id, name: member.name });
      }
      return acc;
    }, [] as { id: string; name: string }[]);

    if (members) {
      setAllMembers(members);
    }

    const years = data?.edges?.reduce((acc, edge) => {
      const year = edge?.node?.year;
      if (year && !acc.some((m) => m === year)) {
        acc.push(year);
      }
      return acc;
    }, [] as number[]);

    if (years) {
      setAllYears(years);
    }

    const projects = data?.edges?.reduce((acc, edge) => {
      const managementPackageVariant =
        edge?.node?.managementPackageVariant?.code || "unknownProject";
      if (
        managementPackageVariant &&
        !acc.some((m) => m === managementPackageVariant)
      ) {
        acc.push(managementPackageVariant);
      }
      return acc;
    }, [] as string[]);

    if (projects) {
      setAllProjects(projects);
    }

    const plannedDates = data?.edges?.reduce((acc, edge) => {
      const plannedDate = edge?.node?.plannedDate;
      const status = edge?.node?.state;
      if (plannedDate && status && !acc.some((pd) => pd.date === plannedDate)) {
        acc.push({ date: plannedDate, state: status });
      }
      return acc;
    }, [] as PlannedDate[]);

    if (plannedDates) {
      setAllPlannedDates(plannedDates);
    }
  }, [
    data?.edges,
    setAllYears,
    setAllMembers,
    setAllProjects,
    setAllPlannedDates,
  ]);
}

export { useFilteredData, usePopulateFilters };
